// Which categories does this visitor actually browse?
//
// The app already keeps a "recently viewed" list, but that is a different
// signal: it holds {id, viewedAt} pairs with a 30-minute TTL, deliberately
// short so the rail on the homepage shows what you were just looking at.
// Personalised recommendations need the opposite -- a slow-moving picture of
// what someone keeps coming back to over days and weeks. So this is its own
// store rather than a reinterpretation of that one.
//
// Device-local by design. It works signed-out, needs no endpoint, and never
// leaves the browser, which also means there is nothing here to leak: the
// only thing recorded is a category name and a count.

const KEY = 'acymart_category_affinity';

// A view stops counting after this long. Someone who spent a week buying
// camera gear in March should not still be shown cameras in June.
const HALF_LIFE_DAYS = 21;

// Enough signal to trust the ranking. Below this the visitor is treated as
// new and gets the everyday-essentials fallback instead of a category they
// happened to tap into twice.
const MIN_VIEWS_FOR_AFFINITY = 3;

// Shown to a first-time visitor, and to anyone whose history is too thin to
// rank. These are the categories a general store sells most of regardless of
// who is looking -- the "everyday products" case.
export const EVERYDAY_CATEGORIES = ['Home', 'Beauty', 'Food', 'Accessories'];

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '{}');
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  } catch {
    // Private mode, cleared storage, or someone else's key shape. Treat an
    // unreadable store as an empty one rather than throwing on a page that
    // is only trying to render a product grid.
    return {};
  }
}

function write(data) {
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* Quota or private mode -- recommendations degrade, nothing breaks. */
  }
}

/**
 * Record that a product in `category` was opened.
 * Safe to call on every product view; it is a single localStorage write.
 */
export function recordCategoryView(category) {
  if (!category || typeof category !== 'string') return;
  const data = read();
  const prev = data[category] || { views: 0, lastAt: 0 };
  data[category] = {
    views: prev.views + 1,
    lastAt: Date.now(),
  };
  write(data);
}

/**
 * Categories ranked by how much this visitor looks at them, strongest first.
 *
 * Recency-weighted rather than a raw count: each category's score decays by
 * half every HALF_LIFE_DAYS since it was last opened. Without that, a
 * category browsed heavily once would outrank the one being browsed today
 * essentially forever.
 *
 * Returns [] when the history is too thin to be meaningful, which is the
 * caller's cue to fall back to EVERYDAY_CATEGORIES.
 */
export function getTopCategories(limit = 3) {
  const data = read();
  const now = Date.now();
  const halfLifeMs = HALF_LIFE_DAYS * 24 * 60 * 60 * 1000;

  const scored = Object.entries(data)
    .map(([category, v]) => {
      const views = Number(v?.views) || 0;
      const lastAt = Number(v?.lastAt) || 0;
      const ageMs = Math.max(0, now - lastAt);
      const decay = Math.pow(0.5, ageMs / halfLifeMs);
      return { category, views, score: views * decay };
    })
    .filter((x) => x.score > 0);

  const totalViews = scored.reduce((sum, x) => sum + x.views, 0);
  if (totalViews < MIN_VIEWS_FOR_AFFINITY) return [];

  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.category);
}

/**
 * The categories to build "Great finds for you" from: the visitor's own if
 * there is enough history, otherwise the everyday fallback.
 *
 * @returns {{ categories: string[], personalised: boolean }}
 */
export function getFeedCategories(limit = 3) {
  const top = getTopCategories(limit);
  return top.length
    ? { categories: top, personalised: true }
    : { categories: EVERYDAY_CATEGORIES.slice(0, limit), personalised: false };
}

/** Clears the stored history. Exposed for a "reset recommendations" control. */
export function clearCategoryAffinity() {
  try { localStorage.removeItem(KEY); } catch { /* nothing to do */ }
}
